import React, { useState, useEffect } from 'react'
import { Popup } from 'reactjs-popup'
import { ErrorMessage, Field, Formik, Form } from 'formik'
import * as Yup from 'yup'
import { FaSpinner } from 'react-icons/fa'
import { getCMSUserById } from '../../../../Services/CmsUserManagement'
import { Loader } from '../../Elements/UpdateLoader'
import { capitalizeFirstLetter } from '../../../CommonComponents/FirstLetterCapital'

// const tabs = [
//   'cms-roles',
//   'roles',
//   'bank-roles',
//   'bank',
//   'branch',
//   'loan',
//   'user',
//   'organization',
//   'pod',
//   'blog',
//   'payment'
// ]

const tabs = {
  cmsRoles: { key: 'cms-roles', name: 'CMS User Management' },
  roles: { key: 'roles', name: 'CMS Roles Management' },
  bankRoles: { key: 'bank-roles', name: 'Bank User Management' },
  bank: { key: 'bank', name: 'Bank Management' },
  branch: { key: 'branch', name: 'Branch Management' },
  loan: { key: 'loan', name: 'Loan Management' },
  user: { key: 'user', name: 'Borrower Individual Management' },
  organization: { key: 'organization', name: 'Borrower Organization Management' },
  pod: { key: 'pod', name: 'Proof Of Delivery' },
  blog: { key: 'blog', name: 'Blog' },
  payment: { key: 'payment', name: 'Payment Management' }
}

const UserActions = ({
  isPreviewOpen,
  onClose,
  userId,
  roles,
  operation,
  handleClose,
  handleSubmit,
  handleUpdate,
  formData,
  isLoadingUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [updateUserValue, setUpdatedUserValues] = useState([])
  const [allChecked, setAllChecked] = useState(false)

  // const roleTabMapping = {
  //   admin: [
  //     'cms-roles',
  //     'roles',
  //     'bank-roles',
  //     'bank',
  //     'branch',
  //     'loan',
  //     'user',
  //     'organization',
  //     'pod',
  //     'blog'
  //   ],
  //   accounts: ['bank', 'branch', 'user', 'organization', 'pod', 'payment'],
  //   agent: ['user', 'organization', 'pod', 'loan'],
  //   supervisor: ['payment', 'bank', 'branch', 'roles', 'cms-roles'],
  //   sadmin: ['all']
  // }

  const roleTabMapping = {
    admin: [
      tabs.cmsRoles,
      tabs.roles,
      tabs.bankRoles,
      tabs.bank,
      tabs.branch,
      tabs.loan,
      tabs.user,
      tabs.organization,
      tabs.pod,
      tabs.blog
    ],
    accounts: [
      tabs.bank,
      tabs.branch,
      tabs.user,
      tabs.organization,
      tabs.pod,
      tabs.payment
    ],
    agent: [tabs.user, tabs.organization, tabs.pod, tabs.loan],
    supervisor: [
      tabs.payment,
      tabs.bank,
      tabs.branch,
      tabs.roles,
      tabs.cmsRoles
    ],
    sadmin: Object.values(tabs) // Assign all tabs to sadmin
  }

  useEffect(() => {
    if (operation === 'update') {
      const getUserById = async () => {
        setIsLoading(true)
        try {
          const data = await getCMSUserById(userId)
          let permission
          if (data?.permissions.includes('*')) {
            permission = ['*']
            setAllChecked(true)
          } else {
            permission = data?.permissions
          }
          setUpdatedUserValues({
            email: data?.email,
            name: data?.name,
            phone_number: data?.phone_number,
            gender: data?.gender,
            role: data?.role.toLowerCase(),
            permission: permission
          })
          setIsLoading(false)
        } catch (error) {
          setIsLoading(false)
          console.error('Error retrieving user data:', error)
        }
      }
      getUserById()
      setIsEditing(true)
    }
  }, [operation])

  // Auto-select tabs based on role
  useEffect(() => {
    if (updateUserValue.role) {
      const defaultTabs =
        updateUserValue.role === 'sadmin'
          ? Object.values(tabs).map(tab => tab.key) // Ensure array format
          : roleTabMapping[updateUserValue.role]?.map(tab => tab.key) || [];
  
      setUpdatedUserValues(prevState => ({
        ...prevState,
        permission: defaultTabs
      }));
  
      setAllChecked(updateUserValue.role === 'sadmin');
    }
  }, [updateUserValue.role]);
  

  const handleCheckboxChange = e => {
    const { value, checked } = e.target
    let updatedPermissions = [...(updateUserValue.permission || [])]

    if (value === 'all') {
      setAllChecked(checked)
      updatedPermissions = checked ? ['*'] : []
    } else {
      if (checked) {
        updatedPermissions.push(value)
      } else {
        updatedPermissions = updatedPermissions.filter(
          permission => permission !== value
        )
      }
      setAllChecked(false)
    }

    setUpdatedUserValues(prevState => ({
      ...prevState,
      permission: updatedPermissions
    }))
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Entity Name is Required')
      .min(3, 'Must be at least 3 characters'),
    email: Yup.string()
      .required('Email is Required')
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Email is Invalid'
      ),
    phone_number: Yup.string()
      .required('Mobile Number is Required')
      .matches(/^[0-9]{10}$/, 'Mobile Number is Invalid'),
    gender: Yup.string().required('Gender is Required'),
    role: Yup.string().required('Role is Required')
  })

  return (
    <>
      <Popup open={isPreviewOpen} modal closeOnDocumentClick={false}>
        {close => (
          <div
            className='bg-gray-500 fixed inset-0 flex bg-opacity-50  h-screen justify-center items-center overflow-y-scroll'
            style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
          >
            {isLoading ? (
              Loader
            ) : (
              <div className='flex container justify-center items-center mt-32 p-8 rounded-md mx-auto min-h-screen max-h-screen '>
                <div className='w-[900px] mx-auto'>
                  <Formik
                    initialValues={isEditing ? updateUserValue : formData}
                    validationSchema={validationSchema}
                    onSubmit={values =>
                      isEditing
                        ? handleUpdate(values, updateUserValue.permission)
                        : handleSubmit(values, updateUserValue.permission)
                    }
                  >
                    {formikProps => (
                      <Form>
                        <div className='bg-bg-form p-12 mb-10 text-tbl-txt-clr'>
                          <div className='flex justify-end'>
                            <button
                              type='button'
                              onClick={() => {
                                handleClose()
                                close()
                                setUpdatedUserValues(null)
                              }}
                              className='text-2xl font-bold'
                            >
                              X
                            </button>
                          </div>
                          <h2 className='text-xl font-semibold pb-6'>
                            {isEditing ? 'Update CMS User' : 'Add CMS User'}
                          </h2>
                          <div className='field-split'>
                            <div className='mb-6'>
                              <label
                                htmlFor='email'
                                className='block text-sm font-medium'
                              >
                                Authorized Email ID
                                <span className='text-red-600 font-bold'>
                                  *
                                </span>
                              </label>
                              <Field
                                type='text'
                                id='email'
                                name='email'
                                placeholder='Enter email Id'
                                className='input-style'
                              />
                              <ErrorMessage
                                name='email'
                                component='div'
                                className='text-red-600 font-semibold'
                              />
                            </div>
                            <div className='mb-6'>
                              <label
                                htmlFor='phone_number'
                                className='block text-sm font-medium'
                              >
                                Authorized Phone Number
                                <span className='text-red-600 font-bold'>
                                  *
                                </span>
                              </label>
                              <Field
                                type='text'
                                id='phone_number'
                                name='phone_number'
                                placeholder='Enter phone number'
                                maxLength={10}
                                className='input-style'
                                onInput={e => {
                                  e.target.value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ''
                                  )
                                }}
                              />
                              <ErrorMessage
                                name='phone_number'
                                component='div'
                                className='text-red-600 font-semibold'
                              />
                            </div>
                          </div>
                          <div className='grid md:grid-cols-4 md:gap-5 mt-4'>
                            <div className='mb-6 col-span-2'>
                              <label
                                htmlFor='name'
                                className='block text-sm font-medium text-txtclr2'
                              >
                                Authorized Person's Name
                                <span className='text-red-600 font-bold'>
                                  *
                                </span>
                              </label>
                              <Field
                                type='text'
                                placeholder='Enter Name'
                                id='name'
                                name='name'
                                className='input-style'
                              />
                              <ErrorMessage
                                name='name'
                                component='div'
                                className='text-red-600 font-semibold'
                              />
                            </div>

                            <div className='mb-6'>
                              <label
                                htmlFor='gender'
                                className='block text-sm font-medium text-txtclr2'
                              >
                                Gender<span className='text-red-800'>*</span>
                              </label>
                              <Field
                                as='select'
                                id='gender'
                                name='gender'
                                className='input-style'
                              >
                                <option value=''>Select</option>
                                <option value='male'>Male</option>
                                <option value='female'>Female</option>
                                <option value='other'>Other</option>
                              </Field>
                              <ErrorMessage
                                name='gender'
                                component='div'
                                className='text-red-600 font-semibold'
                              />
                            </div>
                            <div className='mb-6'>
                              <label
                                htmlFor='role'
                                className='block text-sm font-medium text-txtclr2'
                              >
                                Role<span className='text-red-800'>*</span>
                              </label>
                              <Field
                                as='select'
                                id='role'
                                name='role'
                                className='input-style'
                                onChange={e => {
                                  const selectedRole = e.target.value
                                  formikProps.setFieldValue(
                                    'role',
                                    selectedRole
                                  ) // Update Formik state
                                  setUpdatedUserValues(prevState => ({
                                    ...prevState,
                                    role: selectedRole
                                  }))
                                }}
                              >
                                <option value=''>Select Role</option>
                                {roles?.map(option => {
                                  const roleDisplayNames = {
                                    supervisor: 'Team Leader / Supervisor',
                                    sadmin: 'Super Admin'
                                  }
                                  const displayName =
                                    roleDisplayNames[option?.name] ||
                                    capitalizeFirstLetter(option?.name)
                                  return (
                                    <option
                                      key={option?._id}
                                      value={option?.name}
                                    >
                                      {displayName}
                                    </option>
                                  )
                                })}
                              </Field>
                              <ErrorMessage
                                name='role'
                                component='div'
                                className='text-red-600 font-semibold'
                              />
                            </div>
                          </div>

                          {/* Role Selection */}
                          <div>
                            <label
                              htmlFor='tab_access'
                              className='block text-sm font-medium text-txtclr2'
                            >
                              Tab Access<span className='text-red-800'>*</span>
                            </label>
                            <div className='ml-5 mb-10 mt-2 grid grid-cols-3 gap-2'>
                              <div className='flex items-center mb-2'>
                                <Field
                                  id='all'
                                  name='tab_access'
                                  value='all'
                                  type='checkbox'
                                  className='w-[12px] h-[12px] text-white bg-[#3160ed] border-[#000000] opacity-[60%] rounded'
                                  checked={allChecked}
                                  onChange={handleCheckboxChange}
                                />
                                <label
                                  htmlFor='all'
                                  className='ms-2 text-sm font-medium text-gray-900 '
                                >
                                  All
                                </label>
                              </div>
                              {Object.values(tabs).map(({ key, name }) => (
                                <div className='flex items-center' key={key}>
                                  <Field
                                    id={key}
                                    name='tab_access'
                                    value={key}
                                    type='checkbox'
                                    className='w-[12px] h-[12px] text-white bg-[#0B1E59] border-[#000000] opacity-[60%] rounded'
                                    checked={
                                      updateUserValue &&
                                      updateUserValue.permission
                                        ? updateUserValue.permission.includes(
                                            key
                                          )
                                        : false
                                    }
                                    onChange={handleCheckboxChange}
                                    disabled={allChecked}
                                  />
                                  <label
                                    htmlFor={key}
                                    className={`${
                                      allChecked
                                        ? 'text-gray-400'
                                        : 'text-gray-900 '
                                    } ms-2 text-sm font-medium`}
                                  >
                                    {name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className='flex justify-center '>
                            <button
                              type='submit'
                              className='px-12 py-2 text-white bg-bgclr w-full rounded-md font-bold hover:bg-bgclr focus:outline-none focus:ring focus:ring-indigo-200'
                            >
                              {isLoadingUpdate ? (
                                <FaSpinner className='animate-spin text-3xl' />
                              ) : (
                                'Submit'
                              )}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            )}
          </div>
        )}
      </Popup>
    </>
  )
}

export default UserActions
